import React, { useEffect, useState, useCallback } from 'react';
import { Col, Input } from 'reactstrap';
import Select from 'react-select';

import { fillTypes } from '../../../utils';
import { custom_select } from 'assets/styles/multiple_select';

// ----------------------------------------------------------------------------
// Definição de tipos usados internamente no componente
// ----------------------------------------------------------------------------

/**
 * @typedef {{
 *   type: string,         // modo de preenchimento: 'FREE_TEXT' | 'CLIENT_FIELD' | 'IMPORT_FIELD'
 *   value: string         // valor associado, seja texto livre ou valor do campo do cliente
 * }} ParamConfig
 *
 * @typedef {{
 *   label: string,        // texto exibido no dropdown (ex: 'Texto Livre', 'Campo de cliente'…)
 *   value: string         // valor “interno” (ex: fillTypes.FREE_TEXT, fillTypes.CLIENT_FIELD…)
 * }} SelectOption
 *
 * @typedef {{
 *   value: string,        // valor do campo do cliente (ex: 'email', 'telefone'…)
 *   label: string         // texto exibido (ex: 'Email', 'Telefone'…)
 * }} ClientFieldOption
 */

// ----------------------------------------------------------------------------
//  Opções estáticas para os tipos de preenchimento
// ----------------------------------------------------------------------------
/** @type {SelectOption[]} */
const types = Object.freeze([
  { label: 'Texto Livre', value: fillTypes.FREE_TEXT },
  { label: 'Campo de cliente', value: fillTypes.CLIENT_FIELD },
  { label: 'Campo de importação', value: fillTypes.IMPORT_FIELD }
]);

const TemplateMessageFillingItem = ({
  paramIdendifier,
  clientFields,
  templateParams,
  onChangeParam,
  isUpdate
}) => {
  /**
   * Qual modo está atualmente selecionado?
   * @type {[string, React.Dispatch<React.SetStateAction<string>>]}
   *
   * - Se isUpdate for true e houver templateParams[paramIdendifier],
   *   inicializamos com o valor salvo (ex: 'CLIENT_FIELD').
   * - Caso contrário, usamos o padrão fillTypes.FREE_TEXT.
   */
  const [type, setType] = useState(
    isUpdate && templateParams[paramIdendifier]
      ? templateParams[paramIdendifier].type
      : fillTypes.FREE_TEXT
  );

  /**
   * @type {[boolean, React.Dispatch<React.SetStateAction<boolean>>]}
   * - Se true, significa que trocamos de tipo e precisamos limpar o campo anterior.
   * - Inicializamos como !isUpdate (se for criação, já limpamos; se for edição, não).
   */
  const [shouldReset, setShouldReset] = useState(!isUpdate);

  useEffect(() => {
    if (shouldReset) {
      changeParamValue('');
    } else {
      setShouldReset(true);
    }
  }, [type]);

  /**
   * Quando o usuário escolhe uma opção no Select de tipos, atualiza o estado.
   *
   * @param {{ value?: string }} itemData
   *   O objeto vindo do react-select (tem forma { label, value }).
   *   Se for nulo ou value indefinido, usamos FREE_TEXT como fallback.
   */
  const changeType = useCallback((itemData) => {
    setType(itemData || fillTypes.FREE_TEXT);
    setShouldReset(false);
  }, []);

  const changeParamValue = (value) => {
    onChangeParam(paramIdendifier, {
      type,
      value
    });
  };

  const getDefaultType = () => {
    if (!isUpdate) return types[0];
    const saved = templateParams[paramIdendifier];
    if (!saved) return types[0];

    return types.find((t) => t.value === saved.type) || types[0];
  };

  const getSelectDefaultValue = () => {
    if (!isUpdate) return '';
    const savedValue = templateParams[paramIdendifier]?.value;
    return clientFields.find((field) => field.value === savedValue) || null;
  };

  return (
    <>
      {/* -------------- coluna que exibe “{{1}}”, “{{2}}” -------------- */}
      <div
        md={2}
        lg={1}
        className="d-flex flex-column justify-content-center text-center pl-3"
      >
        {paramIdendifier}
      </div>

      {/* -------------- coluna do dropdown de tipo -------------- */}
      <Col md={6} lg={4}>
        <Select
          styles={custom_select}
          placeholder="Selecione um tipo"
          options={types}
          isSearchable={false}
          isClearable={false}
          name="fillingType"
          defaultValue={getDefaultType()}
          onChange={(e) => {
            changeType(e.value);
            onChangeParam(paramIdendifier, {
              type: e.value,
              value: templateParams[paramIdendifier]?.value || ''
            });
          }}
        />
      </Col>

      {/* -------------- coluna do campo de preenchimento -------------- */}
      <Col md={6} lg={4}>
        {type === fillTypes.CLIENT_FIELD ? (
          <Select
            styles={custom_select}
            placeholder="Selecione um campo"
            options={clientFields || []}
            isSearchable={false}
            isClearable={false}
            name="clientField"
            defaultValue={getSelectDefaultValue()}
            onChange={(item) => changeParamValue(item.value)}
          />
        ) : (
          // Se for “Texto Livre” ou “Import Field”, mostramos um <Input type="text">
          <Input
            name={type === fillTypes.IMPORT_FIELD ? 'importField' : 'freeText'}
            defaultValue={templateParams[paramIdendifier]?.value || ''}
            onChange={(e) => changeParamValue(e.target.value)}
            type="text"
            placeholder={
              type === fillTypes.IMPORT_FIELD
                ? 'Digite o valor de importação'
                : 'Digite texto livre'
            }
            style={{ height: '38px', top: '1px' }}
          />
        )}
      </Col>
    </>
  );
};

export default TemplateMessageFillingItem;
